import { useEffect, useState } from 'react';
import type { IPortfolioCodeQualityDistribution } from '@skystone/shared';
import { ErrorBanner, Spinner } from '@/components/ui';
import { portfolioCodeQualityApi } from '@/services/portfolio-code-quality-api';
import type { IMetricViewFilters } from '@/hooks/use-metric-view-filters';
import { FailingQualityGatePanel } from './FailingQualityGatePanel';
import { LowestCoveragePanel } from './LowestCoveragePanel';
import { WorstReliabilityPanel } from './WorstReliabilityPanel';

interface DistributionColumnSlotProps {
  filters: IMetricViewFilters;
  inScopeRepos: number;
}

/**
 * Distribution column — three ranked panels (Failing QG / Lowest coverage /
 * Worst reliability). Owns its own fetch (`/api/portfolio/code-quality-
 * distribution`) so a slow snapshot query doesn't block the verdict strip,
 * and so it can re-render an inline error banner without the page-level
 * `<Spinner/>` swap. PRODOPSAI-333.
 */
export function DistributionColumnSlot({
  filters,
  inScopeRepos,
}: Readonly<DistributionColumnSlotProps>) {
  const [data, setData] = useState<IPortfolioCodeQualityDistribution | null | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setData(undefined);
    setError(null);

    portfolioCodeQualityApi
      .getDistribution({
        from: filters.from,
        to: filters.to,
        productId: filters.productId || undefined,
        teamId: filters.teamId || undefined,
      })
      .then((response) => {
        if (cancelled) return;
        setData(response);
      })
      .catch(() => {
        if (cancelled) return;
        setError('Failed to load distribution data. Please try again.');
        setData(null);
      });

    return () => {
      cancelled = true;
    };
  }, [filters.from, filters.to, filters.productId, filters.teamId]);

  if (data === undefined) {
    return (
      <div
        className="flex items-center justify-center py-12"
        data-testid="code-quality-distribution-column"
      >
        <Spinner />
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="code-quality-distribution-column">
      {error && <ErrorBanner message={error} />}
      {data && (
        <>
          <FailingQualityGatePanel rows={data.failingQg} inScopeRepos={inScopeRepos} />
          <LowestCoveragePanel rows={data.lowestCoverage} />
          <WorstReliabilityPanel rows={data.worstReliability} />
        </>
      )}
    </div>
  );
}
